import { VocalLib, type CaptureDiagnosis, type PitchFrame } from '@tunasong/music-lib'
import type { PracticeTarget } from './practice-target.js'

/**
 * A take's frames, scored against its target (Pitch Trainer Phase 2, plan M6a). The same steps as Phase 1:
 * the voicing gate, note segmentation, then the phrase score. Nothing heard gives no score, never a zero,
 * and a diagnosis of why (pitfall 9).
 */

export interface ScoredCapture {
  score: VocalLib.PhraseScore | null
  /** Only when nothing was heard */
  diagnosis: CaptureDiagnosis | null
}

/**
 * The sung notes scored against the target's merged reference events (a melody phrase's, or a chord line's). The
 * take's repeats are merged the same way first: a note re-sung within a beat is the note the reference holds
 * (plan M4 step 4.3; Bowie, #2440). Pitch Trainer scores no timing, so a held note and a re-articulated one are the
 * same, and nothing needs forgiving. A beat is the audition's, the pace the singer follows.
 */
export const scoreSung = (sung: VocalLib.SungNote[], target: PracticeTarget) =>
  VocalLib.scorePhrase(VocalLib.mergeSungRepeats(sung, 60 / target.audition.tempo.bpm), target.reference)

export const scoreCapture = (frames: PitchFrame[], target: PracticeTarget, sampleRate: number): ScoredCapture => {
  const score = scoreSung(VocalLib.segmentNotes(VocalLib.voicedFrames(frames), { sampleRate }), target)
  return score ? { score, diagnosis: null } : { score: null, diagnosis: VocalLib.diagnoseCapture(frames) }
}
